import { useEffect, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from '@App/hooks/index'
import { Events } from '@App/sockets/types'

import { setUserOrdersConnection, unsubscribeUserOrders } from '@Redux/slices/sockets/ProxyServiceConnectionSlice'
import {
  getTickerDataAll,
  subscribeToPublicService,
  unsubscribeToPublicService,
} from '@Redux/slices/sockets/PublicServiceConnectionSlice'
import {
  selectAccountsOrdersList,
  selectAccountsTradesList,
  selectOrdersList,
  selectTradesList,
} from '@Redux/slices/trade/trades/selectors'
import { Market, Order, Trade } from '@Redux/slices/trade/trades/types'
import { allWallets, currentWallet } from '@Redux/slices/user/selectors'

import { ITickerEntity, ITickerEntityData, Wallet } from '@Interfaces/CommonInterfaces'

type OrderCalcResult = {
  pnl: number
  usedMargin: number
  usedBalance: number
}

export type BalanceIndicatorKey = 'available' | 'equity' | 'funds' | 'pnl' | 'margin' | 'margin_level'

const calculateOrder = (order: Order | Trade, rate: number): OrderCalcResult => {
  const sign = order.action_type === 'sell' ? -1 : 1
  const usedBalance = Number((order.open_rate * order.quantity) / order.leverage)

  if (order.status === 'closed' || order.status === 'liquidated') {
    if ('close_rate' in order && order.close_rate != null) {
      const realizedPnl = (order.close_rate - order.open_rate) * order.quantity * sign
      return {
        pnl: realizedPnl,
        usedMargin: 0,
        usedBalance,
      }
    }

    const fallbackPnl = (rate - order.open_rate) * order.quantity * sign
    return {
      pnl: fallbackPnl,
      usedMargin: 0,
      usedBalance,
    }
  }

  const usedMargin = Number((order.quantity * rate) / order.leverage)
  const pnl = order.type === 'order' ? 0 : Number((rate - order.open_rate) * order.quantity * sign)

  return {
    pnl,
    usedMargin,
    usedBalance,
  }
}

const getMarketRate = (market: Market, entities: ITickerEntity, actionType: string): number => {
  const tickerKey = `${market.source}_${market.target}`
  const tickerData = entities[tickerKey]
  if (!tickerData) return 1

  return actionType === 'buy' ? tickerData.bid : tickerData.ask
}

const createBalanceIndicators = (
  walletAmount: number,
  pnl: number,
  usedMargin: number,
): { key: BalanceIndicatorKey; value: number }[] => {
  const equity = walletAmount + pnl
  const available = Math.max(0, equity - usedMargin)

  return [
    { key: 'available', value: available },
    { key: 'equity', value: equity },
    { key: 'funds', value: walletAmount },
    { key: 'pnl', value: pnl },
    { key: 'margin', value: usedMargin },
    { key: 'margin_level', value: usedMargin === 0 ? 0 : (equity / usedMargin) * 100 },
  ]
}
export const useBalanceInfo = (wallet?: Wallet | null) => {
  const ticker = useAppSelector((state) => getTickerDataAll(state))
  const trades = useAppSelector(selectTradesList)
  const orders = useAppSelector(selectOrdersList)
  const dispatch = useAppDispatch()

  const buildSubscriptionParams = () => {
    const params: Record<string, { component: string; source_currency: string; target_currency: string }> = {}
    const allItems = [...trades, ...orders]

    for (const item of allItems) {
      const key = `${item.market.source}_${item.market.target}`
      if (!params[key]) {
        params[key] = {
          component: 'useBalanceInfo',
          source_currency: item.market.source,
          target_currency: item.market.target,
        }
      }
    }
    return params
  }

  useEffect(() => {
    const params = buildSubscriptionParams()
    const paramValues = Object.values(params)

    paramValues.forEach((p) => {
      dispatch(
        subscribeToPublicService({
          event: Events.GetRate,
          params: p,
        }),
      )
    })

    return () => {
      const cleanupParams = buildSubscriptionParams()
      Object.values(cleanupParams).forEach((p) => {
        dispatch(
          unsubscribeToPublicService({
            event: Events.GetRate,
            params: p,
          }),
        )
      })
    }
  }, [orders, trades])

  return useMemo(() => {
    if (!wallet || Object.keys(ticker.entities).length === 0) {
      return createBalanceIndicators(0, 0, 0)
    }

    const { totalPnL, totalUsedMargin, totalUsedBalance } = trades.reduce(
      (acc, trade) => {
        const rate = getMarketRate(trade.market, ticker.entities, trade.action_type)
        const result = calculateOrder(trade, rate)

        return {
          totalPnL: acc.totalPnL + result.pnl,
          totalUsedMargin: acc.totalUsedMargin + result.usedMargin,
          totalUsedBalance: acc.totalUsedBalance + result.usedBalance,
        }
      },
      { totalPnL: 0, totalUsedMargin: 0, totalUsedBalance: 0 },
    )

    const { orderTotalUsedMargin, totalUsedBalanceForOrders } = orders.reduce(
      (acc, order) => {
        const rate = getMarketRate(order.market, ticker.entities, order.action_type)
        const result = calculateOrder(order, rate)

        return {
          orderTotalUsedMargin: acc.orderTotalUsedMargin + result.usedMargin,
          totalUsedBalanceForOrders: acc.totalUsedBalanceForOrders + result.usedBalance,
        }
      }, { orderTotalUsedMargin: 0, totalUsedBalanceForOrders: 0 },
    )

    return createBalanceIndicators(wallet.amount + totalUsedBalance + totalUsedBalanceForOrders, totalPnL, totalUsedMargin + orderTotalUsedMargin)
  }, [wallet, ticker, trades, orders])
}

export const useAllWalletsBalanceIndicators = () => {
  const ticker = useAppSelector((state) => getTickerDataAll(state))
  const currWallet = useAppSelector(currentWallet)
  const wallets = useAppSelector((state) => allWallets(state))
  const trades = useAppSelector((state) => selectAccountsTradesList(state))
  const orders = useAppSelector((state) => selectAccountsOrdersList(state))

  const dispatch = useAppDispatch()

  useEffect(() => {
    const walletsWithoutCurrent = wallets?.filter(wallet => wallet.id != currWallet?.id)

    walletsWithoutCurrent?.forEach(wallet => {
      dispatch(setUserOrdersConnection({ accountId: Number(wallet.id), isEdit: false }))
    })

    return () => {
      walletsWithoutCurrent?.forEach(wallet => {
        dispatch(unsubscribeUserOrders(Number(wallet.id)))
      })
    }
  }, [wallets, currWallet])

  return useMemo(() => {
    if (!wallets || wallets.length === 0 || Object.keys(ticker.entities).length === 0) {
      return {}
    }

    const result: Record<number, ReturnType<typeof createBalanceIndicators>> = {}

    wallets.forEach((wallet) => {
      const walletOwnTrades = trades[wallet.id] ?? []

      const { totalPnL, totalUsedMargin, totalUsedBalance } = walletOwnTrades.reduce(
        (acc, trade) => {
          const rate = getMarketRate(trade.market, ticker.entities, trade.action_type)

          const res = calculateOrder(trade, rate)
          return {
            totalPnL: acc.totalPnL + res.pnl,
            totalUsedMargin: acc.totalUsedMargin + res.usedMargin,
            totalUsedBalance: acc.totalUsedBalance + res.usedBalance,
          }
        },
        { totalPnL: 0, totalUsedMargin: 0, totalUsedBalance: 0 },
      )

      const walletOwnOrders = orders[wallet.id] ?? []

      const { orderTotalUsedMargin, totalUsedBalanceForOrders } = walletOwnOrders.reduce(
        (acc, order) => {
          const rate = getMarketRate(order.market, ticker.entities, order.action_type)
          const result = calculateOrder(order, rate)

          return {
            orderTotalUsedMargin: acc.orderTotalUsedMargin + result.usedMargin,
            totalUsedBalanceForOrders: acc.totalUsedBalanceForOrders + result.usedBalance,
          }
        }, { orderTotalUsedMargin: 0, totalUsedBalanceForOrders: 0 },
      )

      result[wallet.id as any as number] = createBalanceIndicators(wallet.amount + totalUsedBalance + totalUsedBalanceForOrders, totalPnL, totalUsedMargin + orderTotalUsedMargin)
    })

    return result
  }, [wallets, orders, trades, ticker])
}

export default useBalanceInfo